import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { isPlatformAdmin } from '@/lib/platform-admin'
import { verifyAdminAccess } from '@/requests/auth'
import { useAuthStore } from '@/stores/authStore'

interface AdminGuardProps {
  children: ReactNode
}

export function AdminGuard({ children }: AdminGuardProps) {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.auth.user)
  const isAdmin = isPlatformAdmin(user)
  const [state, setState] = useState<'checking' | 'ok' | 'denied'>('checking')

  useEffect(() => {
    if (!isAdmin) {
      setState('denied')
      return
    }

    let cancelled = false
    verifyAdminAccess().then((ok) => {
      if (cancelled) return
      setState(ok ? 'ok' : 'denied')
    })

    return () => {
      cancelled = true
    }
  }, [isAdmin])

  useEffect(() => {
    if (state === 'denied') {
      navigate({ to: '/', replace: true })
    }
  }, [state, navigate])

  if (state !== 'ok') {
    return (
      <div className='space-y-4 max-w-xl'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-32 w-full' />
      </div>
    )
  }

  return <>{children}</>
}
